// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/serialize";
import { useState } from "react";

import { Checkbox, NoStyleAccordion } from "@aviary";
import { useTaxonomiesQuery } from "@shared/data/queries/TaxonomiesQuery/Taxonomies.query";
import type { Taxonomy } from "@shared/types/graphqlGenerated";

import type { TaxonomyFilter } from "../../../CatalogSearch";

import { SkeletonCategories } from "./SkeletonCategories";
import { hasAnyActiveChild } from "./helper";

import * as styles from "./EmersonAllCategories.styles";

interface Props {
  isCategoryActive: (id: string) => boolean;
  onCategorySelect: (taxonomy: TaxonomyFilter) => void;
  taxonomyIds: string[];
}

const EmersonAllCategories = ({ isCategoryActive, onCategorySelect, taxonomyIds }: Props) => {
  const [openAccordions, setOpenAccordions] = useState({});

  const { data, loading } = useTaxonomiesQuery({
    variables: { levels: 3, storePlatform: "Emerson" },
  });

  const allTaxonomies = data?.viewer?.taxonomies ?? [];

  if (loading) {
    return <SkeletonCategories />;
  }

  const expandOnInit = (taxonomy: Taxonomy) => {
    const isDefaultExpanded = hasAnyActiveChild(taxonomy, taxonomyIds);
    if (isDefaultExpanded) {
      updateOpenAccordions(taxonomy.id);
    }

    return isDefaultExpanded;
  };

  const formatName = (name: String) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

  const updateOpenAccordions = id =>
    openAccordions[id] === undefined && setOpenAccordions({ ...openAccordions, [id]: true });

  const handleChange =
    ({ id, name }) =>
    () => {
      setOpenAccordions({});
      onCategorySelect({ id, name });
    };

  const renderCheckbox = (taxonomy: Taxonomy, labelStyle: SerializedStyles) => {
    const { id, name, depth } = taxonomy;

    return (
      <Checkbox
        id={id}
        type="checkbox"
        checked={isCategoryActive(id)}
        onChange={handleChange({ id, name })}
        isFullWidth
        labelStyle={[
          styles.checkboxContainer,
          depth === 2 ? styles.grandChildCheckbox : labelStyle,
        ]}
      >
        {formatName(name)}
      </Checkbox>
    );
  };

  const renderChildCategories = (taxonomies: Taxonomy[], labelStyle: SerializedStyles) => {
    return taxonomies.map(taxonomy => {
      const anyChildCategories = taxonomy.children?.length > 0;

      if (anyChildCategories) {
        const isDefaultExpanded = expandOnInit(taxonomy);

        return (
          <NoStyleAccordion
            size="small"
            key={taxonomy.id}
            isChevronTrigger
            triggerStyles={[
              styles.accordion.wrapper,
              openAccordions[taxonomy.id] && styles.accordion.open,
            ]}
            isDefaultExpanded={isDefaultExpanded}
            expandedStyles={styles.accordion.expanded}
            chevronTriggerStyles={styles.accordion.chevron}
            onChange={isOpen => setOpenAccordions({ ...openAccordions, [taxonomy.id]: isOpen })}
            coreTriggerContent={renderCheckbox(taxonomy, labelStyle)}
          >
            {renderChildCategories(taxonomy.children, styles.childCheckbox)}
          </NoStyleAccordion>
        );
      }

      return <div key={taxonomy.id}>{renderCheckbox(taxonomy, labelStyle)}</div>;
    });
  };

  return (
    <div css={styles.scrollContainer}>
      {renderChildCategories(allTaxonomies, styles.checkboxContainer)}
    </div>
  );
};

export { EmersonAllCategories };
